import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    User,
    Users,
    Mail,
    Phone,
    Calendar,
    GraduationCap,
    MapPin,
    BadgeCheck,
    Camera,
    IdCard,
} from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import normalizePhoneNumber from "../utils/normalizePhoneNumber";
import { decryptToken } from "../utils/getToken";
import AdmissionCenterSelect from "./AdmissionCenterSelect";
import { safeResponseJson, normalizeAdmissionBatchIdForApi } from "../../utils/admissionCenters";
import { admissionAssetUrl, resolveAdmissionDocumentsForSave, isPdfPath } from "./admissionFormUpload";
import {
    ADMISSION_TOAST,
    validateAdmissionForm,
    validateAdmissionUploadFile,
} from "./admissionFormValidation";
import { toast } from "react-toastify";
import "./AddAdmission.css";

const AddAdmission3 = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const token = decryptToken("accessToken") || "";
    const submittedData = location.state || null;
    const url = API_BASE_URL;
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");

    const [formData, setFormData] = useState({
        id: "",
        name: "",
        fatherName: "",
        motherName: "",
        dob: "",
        gender: "",
        school: "",
        address: "",
        pincode: "",
        mobile: "",
        email: "",
        center: "",
        main_id: isLoggedIn?.main_id || "",
        status: "",
        studentImage: "",
        aadharImage: "",
        batch_id: "",
    });

    const [batchesList, setBatchesList] = useState([]);

    const [studentImageFile, setStudentImageFile] = useState(null);
    const [aadharImageFile, setAadharImageFile] = useState(null);
    const [studentPreview, setStudentPreview] = useState(null);
    const [aadharPreview, setAadharPreview] = useState(null);
    const studentFileRef = useRef(null);
    const aadharFileRef = useRef(null);

    const formatToInputDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    };

    useEffect(() => {
        if (!studentImageFile) {
            setStudentPreview(null);
            return undefined;
        }
        const u = URL.createObjectURL(studentImageFile);
        setStudentPreview(u);
        return () => URL.revokeObjectURL(u);
    }, [studentImageFile]);

    useEffect(() => {
        if (!aadharImageFile || !aadharImageFile.type.startsWith("image/")) {
            setAadharPreview(null);
            return undefined;
        }
        const u = URL.createObjectURL(aadharImageFile);
        setAadharPreview(u);
        return () => URL.revokeObjectURL(u);
    }, [aadharImageFile]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "mobile") {
            setFormData((prev) => ({
                ...prev,
                mobile: normalizePhoneNumber(value.replace(/\D/g, "")),
            }));
        } else if (name === "pincode") {
            setFormData((prev) => ({
                ...prev,
                pincode: value.replace(/\D/g, "").slice(0, 6),
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    /**
     * Hydrate from router state once per navigation — NOT when isLoggedIn?.main_id appears,
     * or a full setFormData runs again and wipes batch_id the user just selected.
     */
    const editSourceId = submittedData?.id != null ? String(submittedData.id) : "";
    const editNavKey = location.key;

    useEffect(() => {
        if (!submittedData) return;
        const s = submittedData;
        setFormData({
            id: s.id || "",
            name: s.name || "",
            fatherName: s.fatherName || "",
            motherName: s.motherName || "",
            dob: formatToInputDate(s.dob),
            gender: s.gender || "",
            school: s.school || "",
            address: s.address || "",
            pincode: s.pincode || "",
            mobile: s.mobile || "",
            email: s.email || "",
            center: s.center || "",
            main_id: isLoggedIn?.main_id || "",
            status: s.status || "",
            studentImage: s.studentImage || "",
            aadharImage: s.aadharImage || "",
            batch_id:
                s.batch_id != null && s.batch_id !== "" ? String(s.batch_id) : "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit isLoggedIn?.main_id
    }, [editNavKey, editSourceId]);

    useEffect(() => {
        const mid = isLoggedIn?.main_id;
        if (mid == null || mid === "") return;
        setFormData((prev) => (prev.main_id === mid ? prev : { ...prev, main_id: mid }));
    }, [isLoggedIn?.main_id]);

    useEffect(() => {
        if (!formData.center || !token) {
            setBatchesList([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${url}/admission/batches/for-admission?center=${encodeURIComponent(formData.center)}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                const data = await safeResponseJson(res);
                if (cancelled) return;
                if (!res.ok) {
                    setBatchesList([]);
                    return;
                }
                setBatchesList(Array.isArray(data.batches) ? data.batches : []);
            } catch {
                if (!cancelled) setBatchesList([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [formData.center, token, url]);

    const onPickStudentImage = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        const err = validateAdmissionUploadFile(f, { imageOnly: true });
        if (err) {
            toast.error(err, ADMISSION_TOAST);
            return;
        }
        setStudentImageFile(f);
    };

    const onPickAadharDoc = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        const err = validateAdmissionUploadFile(f, { imageOnly: false });
        if (err) {
            toast.error(err, ADMISSION_TOAST);
            return;
        }
        setAadharImageFile(f);
    };

    const clearStudentImage = () => {
        setStudentImageFile(null);
        setFormData((prev) => ({ ...prev, studentImage: "" }));
    };

    const clearAadharImage = () => {
        setAadharImageFile(null);
        setFormData((prev) => ({ ...prev, aadharImage: "" }));
    };

    const storedStudentUrl =
        !studentPreview && formData.studentImage && !isPdfPath(formData.studentImage)
            ? admissionAssetUrl(url, formData.studentImage)
            : null;
    const storedAadharUrl = formData.aadharImage ? admissionAssetUrl(url, formData.aadharImage) : null;
    const todayDateMax = new Date().toISOString().split("T")[0];

    const validateForm = () => {
        const fileErrors = [];
        if (studentImageFile) {
            const err = validateAdmissionUploadFile(studentImageFile, { imageOnly: true });
            if (err) fileErrors.push(`Student photo: ${err}`);
        }
        if (aadharImageFile) {
            const err = validateAdmissionUploadFile(aadharImageFile, { imageOnly: false });
            if (err) fileErrors.push(`Aadhaar document: ${err}`);
        }
        if (fileErrors.length) {
            toast.error(fileErrors[0], ADMISSION_TOAST);
            return false;
        }
        return validateAdmissionForm(formData, { batchesList });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            let studentImage = formData.studentImage || null;
            let aadharImage = formData.aadharImage || null;

            const docExtra = await resolveAdmissionDocumentsForSave(url, token, {
                studentFile: studentImageFile,
                aadharFile: aadharImageFile,
            });
            if (docExtra.studentImage) studentImage = docExtra.studentImage;
            if (docExtra.aadharImage) aadharImage = docExtra.aadharImage;

            if (!studentImage) studentImage = null;
            if (!aadharImage) aadharImage = null;

            const method = formData.id ? "PUT" : "POST";
            const apiUrl = formData.id ? `${url}/admission/${formData.id}` : `${url}/admission/admin`;

            const body = {
                id: formData.id,
                name: formData.name,
                fatherName: formData.fatherName,
                motherName: formData.motherName,
                dob: formData.dob,
                gender: formData.gender,
                school: formData.school,
                address: formData.address,
                pincode: formData.pincode,
                mobile: formData.mobile,
                email: formData.email,
                center: formData.center,
                main_id: formData.main_id,
                status: formData.status,
                batch_id: normalizeAdmissionBatchIdForApi(formData.batch_id),
                studentImage,
                aadharImage,
            };
            if (docExtra.studentImageDataUrl) body.studentImageDataUrl = docExtra.studentImageDataUrl;
            if (docExtra.aadharImageDataUrl) body.aadharImageDataUrl = docExtra.aadharImageDataUrl;

            const payload = formData.id
                ? body
                : {
                      ...body,
                      main_id: isLoggedIn?.main_id,
                      user_name: isLoggedIn?.full_name || isLoggedIn?.email || "Admin",
                      rulesAgreed: "1",
                  };

            const res = await fetch(apiUrl, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await res.json();

            if (res.ok) {
                toast.success(`Admission ${formData.id ? "updated" : "created"} successfully`, {
                    ...ADMISSION_TOAST,
                    autoClose: 2500,
                });
                navigate("/admission");
            } else {
                toast.error(result.message || result.error || "Submission failed", ADMISSION_TOAST);
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Server error", ADMISSION_TOAST);
        }
    };

    return (
        <div className="admission-form-shell">
            <div className="admission-form-shell__bg" aria-hidden />
            <div className="admission-form-container">
                <header className="admission-form-header">
                    <button
                        type="button"
                        className="admission-form-back"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Back
                    </button>
                    <div className="admission-form-header-text">
                        <h1>Edit admission</h1>
                        <p>
                            {formData.id
                                ? "Update student details and enrollment. Changes apply immediately after save."
                                : "No admission is loaded. Use Edit from the admissions list, or create a new record from New admission."}
                        </p>
                    </div>
                </header>

                <div className="admission-form-card">
                    <form className="admission-form-premium" onSubmit={handleSubmit} noValidate>
                        <section className="admission-form-section" aria-labelledby="af-edit-section-student">
                            <h2 id="af-edit-section-student" className="admission-form-section__title">
                                <User size={16} strokeWidth={2.25} aria-hidden />
                                Student details
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field admission-form-field--full">
                                    <div className="admission-file-upload">
                                        <span className="admission-file-upload__label" id="af-edit-student-photo-label">
                                            <Camera
                                                size={14}
                                                style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                                aria-hidden
                                            />
                                            Student photo <span className="admission-file-upload__hint">(optional)</span>
                                        </span>
                                        <p className="admission-file-upload__hint">JPG, PNG, or WebP — max 5 MB.</p>
                                        <input
                                            ref={studentFileRef}
                                            id="af-edit-student-file"
                                            className="admission-file-upload__visually-hidden"
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/jpg"
                                            aria-labelledby="af-edit-student-photo-label"
                                            onChange={onPickStudentImage}
                                        />
                                        <label htmlFor="af-edit-student-file" className="admission-file-upload__zone admission-file-upload__zone--photo">
                                            {studentPreview || storedStudentUrl ? (
                                                <img
                                                    className="admission-file-upload__preview admission-file-upload__preview--avatar"
                                                    src={studentPreview || storedStudentUrl}
                                                    alt=""
                                                />
                                            ) : (
                                                <span className="admission-file-upload__hint">
                                                    Click to upload a photo
                                                </span>
                                            )}
                                        </label>
                                        <div className="admission-file-upload__actions">
                                            <button
                                                type="button"
                                                className="admission-file-upload__btn"
                                                onClick={() => studentFileRef.current?.click()}
                                            >
                                                Choose file
                                            </button>
                                            {(studentImageFile || formData.studentImage) && (
                                                <button
                                                    type="button"
                                                    className="admission-file-upload__btn admission-file-upload__btn--danger"
                                                    onClick={clearStudentImage}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-name">
                                        Full name<span className="req">*</span>
                                    </label>
                                    <input
                                        id="af-edit-name"
                                        className="admission-form-input"
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        maxLength={100}
                                        placeholder="Student name"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-father">
                                        Father&apos;s name
                                    </label>
                                    <input
                                        id="af-edit-father"
                                        className="admission-form-input"
                                        type="text"
                                        name="fatherName"
                                        value={formData.fatherName}
                                        onChange={handleInputChange}
                                        placeholder="Father name"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-mother">
                                        Mother&apos;s name
                                    </label>
                                    <input
                                        id="af-edit-mother"
                                        className="admission-form-input"
                                        type="text"
                                        name="motherName"
                                        value={formData.motherName}
                                        onChange={handleInputChange}
                                        placeholder="Mother name"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-dob">
                                        <Calendar
                                            size={14}
                                            style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                            aria-hidden
                                        />
                                        Date of birth
                                    </label>
                                    <input
                                        id="af-edit-dob"
                                        className="admission-form-input"
                                        type="date"
                                        name="dob"
                                        value={formData.dob}
                                        onChange={handleInputChange}
                                        max={todayDateMax}
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-gender">
                                        Gender
                                    </label>
                                    <select
                                        id="af-edit-gender"
                                        className="admission-form-select"
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Transgender">Transgender</option>
                                    </select>
                                </div>

                                <div className="admission-form-field admission-form-field--full">
                                    <div className="admission-file-upload">
                                        <span className="admission-file-upload__label" id="af-edit-aadhar-doc-label">
                                            <IdCard
                                                size={14}
                                                style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                                aria-hidden
                                            />
                                            Aadhaar document <span className="admission-file-upload__hint">(optional)</span>
                                        </span>
                                        <p className="admission-file-upload__hint">
                                            Upload a clear photo or PDF of the Aadhaar card — max 5 MB.
                                        </p>
                                        <input
                                            ref={aadharFileRef}
                                            id="af-edit-aadhar-file"
                                            className="admission-file-upload__visually-hidden"
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp,image/jpg,application/pdf"
                                            aria-labelledby="af-edit-aadhar-doc-label"
                                            onChange={onPickAadharDoc}
                                        />
                                        {formData.aadharImage && isPdfPath(formData.aadharImage) && !aadharImageFile ? (
                                            <div className="admission-file-upload__zone">
                                                <a
                                                    className="admission-file-upload__link"
                                                    href={storedAadharUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View current PDF
                                                </a>
                                            </div>
                                        ) : (
                                            <label htmlFor="af-edit-aadhar-file" className="admission-file-upload__zone">
                                                {aadharImageFile ? (
                                                    aadharPreview ? (
                                                        <img
                                                            className="admission-file-upload__preview"
                                                            src={aadharPreview}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <span className="admission-file-upload__filename">{aadharImageFile.name}</span>
                                                    )
                                                ) : formData.aadharImage ? (
                                                    <img
                                                        className="admission-file-upload__preview"
                                                        src={storedAadharUrl}
                                                        alt=""
                                                    />
                                                ) : (
                                                    <span className="admission-file-upload__hint">
                                                        Click to upload image or PDF
                                                    </span>
                                                )}
                                            </label>
                                        )}
                                        <div className="admission-file-upload__actions">
                                            <button
                                                type="button"
                                                className="admission-file-upload__btn"
                                                onClick={() => aadharFileRef.current?.click()}
                                            >
                                                Choose file
                                            </button>
                                            {(aadharImageFile || formData.aadharImage) && (
                                                <button
                                                    type="button"
                                                    className="admission-file-upload__btn admission-file-upload__btn--danger"
                                                    onClick={clearAadharImage}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="af-edit-school">
                                        <GraduationCap
                                            size={14}
                                            style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }}
                                            aria-hidden
                                        />
                                        School
                                    </label>
                                    <input
                                        id="af-edit-school"
                                        className="admission-form-input"
                                        type="text"
                                        name="school"
                                        value={formData.school}
                                        onChange={handleInputChange}
                                        placeholder="School name"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="af-edit-section-contact">
                            <h2 id="af-edit-section-contact" className="admission-form-section__title">
                                <Users size={16} strokeWidth={2.25} aria-hidden />
                                Contact
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-email">
                                        <Mail size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} aria-hidden />
                                        Email
                                    </label>
                                    <input
                                        id="af-edit-email"
                                        className="admission-form-input"
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="name@example.com"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-mobile">
                                        <Phone size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} aria-hidden />
                                        Phone
                                    </label>
                                    <input
                                        id="af-edit-mobile"
                                        className="admission-form-input"
                                        type="tel"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        maxLength={10}
                                        placeholder="10-digit mobile"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="af-edit-section-address">
                            <h2 id="af-edit-section-address" className="admission-form-section__title">
                                <MapPin size={16} strokeWidth={2.25} aria-hidden />
                                Address
                            </h2>
                            <div className="admission-form-grid">
                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="af-edit-address">
                                        Street address
                                    </label>
                                    <input
                                        id="af-edit-address"
                                        className="admission-form-input"
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        placeholder="House, street, area"
                                    />
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-pincode">
                                        Pincode
                                    </label>
                                    <input
                                        id="af-edit-pincode"
                                        className="admission-form-input"
                                        type="text"
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={handleInputChange}
                                        placeholder="PIN"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="admission-form-section" aria-labelledby="af-edit-section-enrollment">
                            <h2 id="af-edit-section-enrollment" className="admission-form-section__title">
                                <BadgeCheck size={16} strokeWidth={2.25} aria-hidden />
                                Center &amp; batch
                            </h2>
                            <p className="admission-file-upload__hint" style={{ marginBottom: 12 }}>
                                Choose the center first, then a batch. Schedule and teacher are stored only on the batch.
                            </p>
                            <div className="admission-form-grid">
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-center">
                                        Center
                                    </label>
                                    <AdmissionCenterSelect
                                        name="center"
                                        value={formData.center}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                center: e.target.value,
                                                batch_id: "",
                                            }))
                                        }
                                        token={token}
                                        className="admission-form-select"
                                    />
                                </div>
                                <div className="admission-form-field admission-form-field--full">
                                    <label className="admission-form-field__label" htmlFor="af-edit-batch">
                                        Batch
                                    </label>
                                    <select
                                        id="af-edit-batch"
                                        className="admission-form-select"
                                        name="batch_id"
                                        value={formData.batch_id}
                                        onChange={handleInputChange}
                                        disabled={!formData.center}
                                    >
                                        <option value="">
                                            {formData.center
                                                ? batchesList.length > 0
                                                    ? "Select batch (required)"
                                                    : "No batches for this center"
                                                : "Select a center first"}
                                        </option>
                                        {formData.batch_id &&
                                            !batchesList.some(
                                                (b) => String(b.id) === String(formData.batch_id)
                                            ) && (
                                                <option value={formData.batch_id}>
                                                    Saved batch #{formData.batch_id} (change center to reload list)
                                                </option>
                                            )}
                                        {batchesList.map((b) => (
                                            <option key={b.id} value={String(b.id)}>
                                                {b.scheduleSummary || b.name}
                                                {typeof b.studentCount === "number" && b.max_students
                                                    ? ` · ${b.studentCount}/${b.max_students}`
                                                    : ""}
                                            </option>
                                        ))}
                                    </select>
                                    {(() => {
                                        const sel = batchesList.find(
                                            (b) => String(b.id) === String(formData.batch_id)
                                        );
                                        return sel ? (
                                            <p className="admission-file-upload__hint" style={{ marginTop: 8 }}>
                                                {sel.scheduleSummary}
                                            </p>
                                        ) : null;
                                    })()}
                                </div>
                                <div className="admission-form-field">
                                    <label className="admission-form-field__label" htmlFor="af-edit-status">
                                        Status
                                    </label>
                                    <select
                                        id="af-edit-status"
                                        className="admission-form-select"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select</option>
                                        <option value="Active">Active</option>
                                        <option value="Deactive">Deactive</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Failed">Failed</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        <footer className="admission-form-footer">
                            <button
                                type="button"
                                className="admission-form-btn admission-form-btn--secondary"
                                onClick={() => navigate("/admission")}
                            >
                                Cancel
                            </button>
                            <button type="submit" className="admission-form-btn admission-form-btn--primary">
                                {formData.id ? "Save changes" : "Create admission"}
                            </button>
                        </footer>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddAdmission3;
